const IDENTITY_VERSION = 1;
const VISITOR_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const VISIT_TIMEOUT_MS = 30 * 60 * 1000;

const STORAGE_KEYS = {
    VISITOR: 'arento.analytics.v2.visitor',
    VISIT: 'arento.analytics.v2.visit',
    LOCK: 'arento.analytics.v2.identity.lock',
    TAB_ID: 'arento.analytics.v2.tabId',
    TAB_SEQUENCE: 'arento.analytics.v2.tabSequence'
};

const LOCK_NAME = 'arento-analytics-v2-identity';
const LOCK_LEASE_MS = 1200;
const LOCK_RETRY_MS = 25;
const LOCK_MAX_ATTEMPTS = 80;
const TAB_CHANNEL_NAME = 'arento.analytics.v2.tabs';

const memoryLocalStorage = createMemoryStorage();
const memorySessionStorage = createMemoryStorage();
const tabInstanceId = createUuid();

let activeTabId = null;
let currentPageView = null;
let tabChannel = null;
let tabIdentityPromise = null;

function createMemoryStorage() {
    const values = new Map();

    return {
        getItem: (key) =>
            values.has(key)
                ? values.get(key)
                : null,

        setItem: (key, value) =>
            values.set(
                key,
                String(value)
            ),

        removeItem: (key) =>
            values.delete(key)
    };
}

function createUuid() {
    const cryptoApi =
        typeof window !== 'undefined'
            ? window.crypto
            : null;

    if (cryptoApi?.randomUUID) {
        return cryptoApi.randomUUID();
    }

    if (cryptoApi?.getRandomValues) {
        const bytes =
            new Uint8Array(16);

        cryptoApi.getRandomValues(
            bytes
        );

        bytes[6] =
            (bytes[6] & 0x0f) | 0x40;

        bytes[8] =
            (bytes[8] & 0x3f) | 0x80;

        return Array
            .from(
                bytes,
                byte =>
                    byte
                        .toString(16)
                        .padStart(2, '0')
            )
            .join('')
            .replace(
                /^(.{8})(.{4})(.{4})(.{4})(.{12})$/,
                '$1-$2-$3-$4-$5'
            );
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
        .replace(/[xy]/g, char => {
            const random =
                Math.floor(
                    Math.random() * 16
                );

            const value =
                char === 'x'
                    ? random
                    : (random & 0x3) | 0x8;

            return value
                .toString(16);
        });
}

function safeParse(value) {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function getLocalStorage() {
    if (
        typeof window ===
        'undefined'
    ) {
        return memoryLocalStorage;
    }

    try {
        const storage =
            window.localStorage;

        const probe =
            '__arento_analytics_v2_probe__';

        storage.setItem(
            probe,
            '1'
        );

        storage.removeItem(
            probe
        );

        return storage;
    } catch (error) {
        return memoryLocalStorage;
    }
}

function getSessionStorage() {
    if (
        typeof window ===
        'undefined'
    ) {
        return memorySessionStorage;
    }

    try {
        const storage =
            window.sessionStorage;

        const probe =
            '__arento_analytics_v2_probe__';

        storage.setItem(
            probe,
            '1'
        );

        storage.removeItem(
            probe
        );

        return storage;
    } catch (error) {
        return memorySessionStorage;
    }
}

function readJson(
    storage,
    key
) {
    return safeParse(
        storage.getItem(key)
    );
}

function writeJson(
    storage,
    key,
    value
) {
    storage.setItem(
        key,
        JSON.stringify(value)
    );
}

function sleep(ms) {
    return new Promise(
        resolve =>
            setTimeout(
                resolve,
                ms
            )
    );
}

function normalizePathname(
    pathname
) {
    if (
        !pathname ||
        pathname === '/'
    ) {
        return '/';
    }

    return pathname
        .replace(
            /\/{2,}/g,
            '/'
        )
        .replace(
            /\/+$/,
            ''
        ) || '/';
}

function getOrCreateStoredTabId() {
    const storage =
        getSessionStorage();

    const existing =
        storage.getItem(
            STORAGE_KEYS.TAB_ID
        );

    if (existing) {
        activeTabId =
            existing;

        return existing;
    }

    const tabId =
        createUuid();

    storage.setItem(
        STORAGE_KEYS.TAB_ID,
        tabId
    );

    storage.setItem(
        STORAGE_KEYS.TAB_SEQUENCE,
        '0'
    );

    activeTabId =
        tabId;

    return tabId;
}

function replaceStoredTabId() {
    const storage =
        getSessionStorage();

    const tabId =
        createUuid();

    storage.setItem(
        STORAGE_KEYS.TAB_ID,
        tabId
    );

    storage.setItem(
        STORAGE_KEYS.TAB_SEQUENCE,
        '0'
    );

    activeTabId =
        tabId;

    return tabId;
}

function getTabChannel() {
    if (
        typeof window ===
        'undefined' ||
        typeof window.BroadcastChannel !==
        'function'
    ) {
        return null;
    }

    if (!tabChannel) {
        tabChannel =
            new window.BroadcastChannel(
                TAB_CHANNEL_NAME
            );

        tabChannel.addEventListener(
            'message',
            event => {
                const message =
                    event?.data;

                if (
                    !message ||
                    message.instanceId ===
                    tabInstanceId
                ) {
                    return;
                }

                if (
                    message.type ===
                    'tab_probe' &&
                    activeTabId &&
                    message.tabId ===
                    activeTabId
                ) {
                    tabChannel.postMessage({
                        type:
                            'tab_duplicate',

                        tabId:
                        activeTabId,

                        instanceId:
                        tabInstanceId,

                        targetInstanceId:
                        message.instanceId
                    });
                }
            }
        );
    }

    return tabChannel;
}

async function ensureUniqueTabId() {
    if (tabIdentityPromise) {
        return tabIdentityPromise;
    }

    tabIdentityPromise =
        (async () => {
            let tabId =
                getOrCreateStoredTabId();

            const channel =
                getTabChannel();

            if (!channel) {
                return tabId;
            }

            for (
                let round = 0;
                round < 3;
                round += 1
            ) {
                let duplicateDetected =
                    false;

                const handleMessage =
                    event => {
                        const message =
                            event?.data;

                        if (
                            !message ||
                            message.instanceId ===
                            tabInstanceId
                        ) {
                            return;
                        }

                        if (
                            message.type ===
                            'tab_duplicate' &&

                            message.tabId ===
                            tabId &&

                            message.targetInstanceId ===
                            tabInstanceId
                        ) {
                            duplicateDetected =
                                true;
                        }
                    };

                channel.addEventListener(
                    'message',
                    handleMessage
                );

                channel.postMessage({
                    type:
                        'tab_probe',

                    tabId,

                    instanceId:
                    tabInstanceId
                });

                await sleep(60);

                channel.removeEventListener(
                    'message',
                    handleMessage
                );

                if (
                    !duplicateDetected
                ) {
                    return tabId;
                }

                tabId =
                    replaceStoredTabId();
            }

            return tabId;
        })();

    return tabIdentityPromise;
}

async function acquireLocalStorageLock(
    ownerToken
) {
    const storage =
        getLocalStorage();

    for (
        let attempt = 0;
        attempt <
        LOCK_MAX_ATTEMPTS;
        attempt += 1
    ) {
        const now =
            Date.now();

        const current =
            readJson(
                storage,
                STORAGE_KEYS.LOCK
            );

        if (
            !current ||
            !current.expiresAt ||
            current.expiresAt <= now
        ) {
            writeJson(
                storage,
                STORAGE_KEYS.LOCK,
                {
                    ownerToken,

                    expiresAt:
                        now +
                        LOCK_LEASE_MS
                }
            );

            await sleep(
                10 +
                Math.floor(
                    Math.random() *
                    10
                )
            );

            const confirmed =
                readJson(
                    storage,
                    STORAGE_KEYS.LOCK
                );

            if (
                confirmed?.ownerToken ===
                ownerToken
            ) {
                return true;
            }
        }

        await sleep(
            LOCK_RETRY_MS +
            Math.floor(
                Math.random() *
                15
            )
        );
    }

    return false;
}

function releaseLocalStorageLock(
    ownerToken
) {
    const storage =
        getLocalStorage();

    const current =
        readJson(
            storage,
            STORAGE_KEYS.LOCK
        );

    if (
        current?.ownerToken ===
        ownerToken
    ) {
        storage.removeItem(
            STORAGE_KEYS.LOCK
        );
    }
}

async function withIdentityLock(
    callback
) {
    if (
        typeof navigator !==
        'undefined' &&
        navigator.locks?.request
    ) {
        try {
            return await navigator
                .locks
                .request(
                    LOCK_NAME,
                    {
                        mode:
                            'exclusive'
                    },
                    callback
                );
        } catch (error) {
            // fallback ниже
        }
    }

    const tabId =
        await ensureUniqueTabId();

    const ownerToken =
        `${tabId}:${createUuid()}`;

    const acquired =
        await acquireLocalStorageLock(
            ownerToken
        );

    if (!acquired) {
        throw new Error(
            'Analytics V2: cannot acquire identity lock'
        );
    }

    try {
        return await callback();
    } finally {
        releaseLocalStorageLock(
            ownerToken
        );
    }
}

function isValidVisitorState(
    state,
    nowMs
) {
    return Boolean(
        state &&

        typeof state.visitorId ===
        'string' &&

        Number.isFinite(
            state.expiresAt
        ) &&

        state.expiresAt >
        nowMs
    );
}

function isValidVisitState(
    state,
    visitorId
) {
    return Boolean(
        state &&

        state.visitorId ===
        visitorId &&

        typeof state.visitId ===
        'string' &&

        Number.isFinite(
            state.lastActivityAt
        )
    );
}

export async function touchAnalyticsIdentity(
    occurredAtMs = Date.now()
) {
    const tabId =
        await ensureUniqueTabId();

    return withIdentityLock(
        async () => {
            const storage =
                getLocalStorage();

            const nowMs =
                Date.now();

            let visitorState =
                readJson(
                    storage,
                    STORAGE_KEYS.VISITOR
                );

            let visitState =
                readJson(
                    storage,
                    STORAGE_KEYS.VISIT
                );

            let visitorChanged =
                false;

            if (
                !isValidVisitorState(
                    visitorState,
                    nowMs
                )
            ) {
                visitorState = {
                    visitorId:
                        createUuid(),

                    identityVersion:
                    IDENTITY_VERSION,

                    createdAt:
                    nowMs,

                    lastSeenAt:
                    nowMs,

                    expiresAt:
                        nowMs +
                        VISITOR_TTL_MS
                };

                visitorChanged =
                    true;
            } else {
                visitorState = {
                    ...visitorState,

                    identityVersion:
                    IDENTITY_VERSION,

                    lastSeenAt:
                        Math.max(
                            visitorState
                                .lastSeenAt ||
                            0,

                            nowMs
                        ),

                    expiresAt:
                        Math.max(
                            visitorState
                                .expiresAt ||
                            0,

                            nowMs +
                            VISITOR_TTL_MS
                        )
                };
            }

            const visitMissingOrForeign =
                !isValidVisitState(
                    visitState,
                    visitorState.visitorId
                );

            const gapMs =
                visitMissingOrForeign
                    ? Number
                        .POSITIVE_INFINITY

                    : occurredAtMs -
                    visitState
                        .lastActivityAt;

            if (
                visitorChanged ||
                visitMissingOrForeign ||
                gapMs >
                VISIT_TIMEOUT_MS
            ) {
                visitState = {
                    visitorId:
                    visitorState
                        .visitorId,

                    visitId:
                        createUuid(),

                    startedAt:
                    occurredAtMs,

                    lastActivityAt:
                    occurredAtMs
                };
            } else {
                visitState = {
                    ...visitState,

                    lastActivityAt:
                        Math.max(
                            visitState
                                .lastActivityAt,

                            occurredAtMs
                        )
                };
            }

            writeJson(
                storage,
                STORAGE_KEYS.VISITOR,
                visitorState
            );

            writeJson(
                storage,
                STORAGE_KEYS.VISIT,
                visitState
            );

            return {
                identityVersion:
                IDENTITY_VERSION,

                visitorId:
                visitorState
                    .visitorId,

                visitId:
                visitState
                    .visitId,

                tabId
            };
        }
    );
}

export async function nextTabSequence() {
    await ensureUniqueTabId();

    const storage =
        getSessionStorage();

    const current =
        Number.parseInt(
            storage.getItem(
                STORAGE_KEYS
                    .TAB_SEQUENCE
            ) || '0',
            10
        );

    const next =
        Number.isFinite(current)
            ? current + 1
            : 1;

    storage.setItem(
        STORAGE_KEYS.TAB_SEQUENCE,
        String(next)
    );

    return next;
}

export function ensurePageView(
    pathname
) {
    const pagePath =
        normalizePathname(
            pathname
        );

    if (
        currentPageView?.pagePath ===
        pagePath
    ) {
        return currentPageView;
    }

    currentPageView = {
        pageViewId:
            createUuid(),

        pagePath,

        startedAt:
            new Date()
                .toISOString()
    };

    return currentPageView;
}

export function getCurrentPageView() {
    if (currentPageView) {
        return currentPageView;
    }

    const pathname =
        typeof window !==
        'undefined'
            ? window.location.pathname
            : '/';

    return ensurePageView(
        pathname
    );
}

export function createAnalyticsUuid() {
    return createUuid();
}

export async function getAnalyticsIdentityContext(
    occurredAtMs = Date.now()
) {
    const identity =
        await touchAnalyticsIdentity(
            occurredAtMs
        );

    return {
        ...identity,
        ...getCurrentPageView()
    };
}

export const ANALYTICS_V2_IDENTITY =
    Object.freeze({
        identityVersion:
        IDENTITY_VERSION,

        visitorTtlMs:
        VISITOR_TTL_MS,

        visitTimeoutMs:
        VISIT_TIMEOUT_MS,

        storageKeys:
        STORAGE_KEYS
    });